import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import clsx from 'clsx'

import Header from '@/components-latest/layout/Header'
import Button from '@/components-latest/primitives/Button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components-latest/primitives/Dialog'
import BuyXGetYIllustration from '../../components-latest/primitives/Icons/BuyXGetYIllustration'
import CartDiscountIllustration from '../../components-latest/primitives/Icons/CartDiscountIllustration'
import ClickRight from '@/components-latest/primitives/Icons/ClickRight'
import CopyIcon from '../../components-latest/primitives/Icons/Copy'
import CopyDone from '../../components-latest/primitives/Icons/CopyDone'
import ItemDiscountIllustration from '../../components-latest/primitives/Icons/ItemDiscountIllustration'
import BarCode from '@/components-latest/primitives/Icons/BarCode'
import Download from '@/components-latest/primitives/Icons/Download'
import Printer from '@/components-latest/primitives/Icons/Printer'
import Text from '@/components-latest/primitives/Text'
import CommonModal from '@/components-latest/shared/Modals/CommonModal'
import { useToaster } from '@/components-latest/ui/Toaster/useToaster/useToaster'
import { selectToken } from '@/ducks/auth/auth.selectors'
import { useFetchCartDiscountsQuery } from '@/ducks/offers/api/offers.api'
import { useGetStoreMarketingProfileQuery } from '@/ducks/store/api/store.api'
import { selectStoreId } from '@/ducks/store/selectors/store.selectors'
import { PATHS } from '@/config/router/routes'
import { getQrCodeDataUrl } from '@/utils/qr-code.utils'

import Styles from './MarketingToolsPage.module.scss'

interface MarketingToolsPageProps {
  onCreateOffer: (offerType: 'item_discount' | 'cart_discount' | 'buyX_getY') => void
}

const fallbackStorePublicUrl = (id: string) => `https://yassir.com/store/${id}`

/** How long “Copied” stays visible before reverting to “Copy”. */
const LINK_COPY_FEEDBACK_MS = 5000

/** Escape text for safe insertion into HTML (text or attribute values). */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

const MarketingToolsPage: React.FC<MarketingToolsPageProps> = ({
  onCreateOffer
}) => {
  const { t } = useTranslation()
  const { addToast } = useToaster()
  const storeId = useSelector(selectStoreId)
  const token = useSelector(selectToken)
  const history = useHistory()
  const { data: cartDiscountsData } = useFetchCartDiscountsQuery()
  const [isQrOpen, setIsQrOpen] = useState(false)
  const [qrImageSrc, setQrImageSrc] = useState('')
  const [linkCopied, setLinkCopied] = useState(false)
  const [showCartDiscountError, setShowCartDiscountError] = useState(false)
  const copyFeedbackTimerRef = useRef<number | null>(null)
  /** False after unmount — avoids DOM/timeouts after teardown. */
  const mountedRef = useRef(true)
  /** Aborts hidden iframe print session (timeouts, iframe, listeners). */
  const abortPrintSessionRef = useRef<(() => void) | null>(null)

  const clearCopyFeedbackTimer = useCallback(() => {
    if (copyFeedbackTimerRef.current != null) {
      clearTimeout(copyFeedbackTimerRef.current)
      copyFeedbackTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      abortPrintSessionRef.current?.()
    }
  }, [])

  useEffect(() => () => clearCopyFeedbackTimer(), [clearCopyFeedbackTimer])

  const skipMarketingProfile = !token
  const { data: marketingProfile, isFetching: isMarketingProfileLoading } =
    useGetStoreMarketingProfileQuery(undefined, {
      skip: skipMarketingProfile
    })

  const storeDeepLink = useMemo(() => {
    if (!storeId) return ''
    if (!skipMarketingProfile && isMarketingProfileLoading) return ''
    const fromApi = marketingProfile?.storeLink?.trim()
    if (fromApi) return fromApi
    return fallbackStorePublicUrl(storeId)
  }, [
    storeId,
    skipMarketingProfile,
    isMarketingProfileLoading,
    marketingProfile?.storeLink
  ])

  const showProfileLoading =
    Boolean(token && storeId) && !skipMarketingProfile && isMarketingProfileLoading

  const handleCreateCartDiscount = () => {
    if ((cartDiscountsData?.length ?? 0) > 0) {
      setShowCartDiscountError(true)
      return
    }

    onCreateOffer('cart_discount')
  }

  const handleGoToActiveDiscounts = () => {
    setShowCartDiscountError(false)
    history.push(PATHS.MARKETING_CAMPAIGNS)
  }

  useEffect(() => {
    if (!storeDeepLink) {
      setQrImageSrc('')
      return
    }
    let cancelled = false
    void getQrCodeDataUrl(storeDeepLink, 232).then(
      dataUrl => {
        if (!cancelled) setQrImageSrc(dataUrl)
      },
      () => {
        if (!cancelled) setQrImageSrc('')
      }
    )
    return () => {
      cancelled = true
    }
  }, [storeDeepLink])

  const handleCopyStoreLink = async () => {
    if (!storeDeepLink) return
    try {
      await navigator.clipboard.writeText(storeDeepLink)
      clearCopyFeedbackTimer()
      setLinkCopied(true)
      copyFeedbackTimerRef.current = window.setTimeout(() => {
        setLinkCopied(false)
        copyFeedbackTimerRef.current = null
      }, LINK_COPY_FEEDBACK_MS)
    } catch {
      addToast({
        type: 'system',
        intent: 'error',
        message: t('marketing.link-sharing.copy-failed')
      })
    }
  }

  const openQrDialog = () => {
    if (storeDeepLink) setIsQrOpen(true)
  }

  const handleDownloadQr = useCallback(async () => {
    if (!qrImageSrc) return
    try {
      const res = await fetch(qrImageSrc, { mode: 'cors' })
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = 'store-qr-code.png'
      anchor.rel = 'noopener'
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(url)
    } catch {
      window.open(qrImageSrc, '_blank', 'noopener,noreferrer')
    }
  }, [qrImageSrc])

  const handlePrintQr = useCallback(() => {
    if (!qrImageSrc || !storeDeepLink) return

    abortPrintSessionRef.current?.()

    const safeTitle = escapeHtml(t('marketing.link-sharing.qr-dialog-title'))
    const safeLink = escapeHtml(storeDeepLink)
    const imgSrcEscaped = qrImageSrc
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')

    const markup = `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/><title>${safeTitle}</title>
<style>
  body{font-family:Gellix,system-ui,sans-serif;text-align:center;padding:28px;color:#2f2b43;margin:0;}
  h1{font-size:18px;font-weight:700;margin:0 0 20px;color:#2f2b43;}
  .wrap{display:inline-block;padding:12px;border:1px solid #edeaf1;border-radius:10px;background:#fff;}
  img{display:block;width:260px;height:260px;margin:0 auto;}
  p{margin-top:18px;font-size:13px;word-break:break-all;color:#2f2b43;max-width:320px;margin-left:auto;margin-right:auto;}
</style></head><body>
<h1>${safeTitle}</h1>
<div class="wrap"><img src="${imgSrcEscaped}" alt="" /></div>
<p>${safeLink}</p>
</body></html>`

    const iframe = document.createElement('iframe')
    iframe.setAttribute(
      'style',
      'position:fixed;right:0;bottom:0;width:1px;height:1px;border:0;opacity:0;pointer-events:none'
    )
    iframe.setAttribute('aria-hidden', 'true')

    const timeoutIds: number[] = []
    let cleanedUp = false

    const clearScheduledTimeouts = () => {
      timeoutIds.forEach(id => window.clearTimeout(id))
      timeoutIds.length = 0
    }

    function cleanup() {
      if (cleanedUp) return
      cleanedUp = true
      clearScheduledTimeouts()
      const w = iframe.contentWindow
      if (w) {
        try {
          w.removeEventListener('afterprint', afterPrint)
        } catch {
          /* noop */
        }
      }
      try {
        iframe.remove()
      } catch {
        /* noop */
      }
      if (abortPrintSessionRef.current === cleanup) {
        abortPrintSessionRef.current = null
      }
    }

    function afterPrint() {
      cleanup()
    }

    abortPrintSessionRef.current = cleanup

    const schedule = (fn: () => void, delay: number) => {
      const id = window.setTimeout(() => {
        if (!mountedRef.current || cleanedUp) return
        fn()
      }, delay)
      timeoutIds.push(id)
    }

    /** Failsafe if afterprint never fires (browser quirks). */
    schedule(cleanup, 120_000)

    const runPrintWhenImageReady = (win: Window, doc: Document) => {
      let attempts = 0
      const maxAttempts = 150

      const waitForQrImageThenPrint = () => {
        if (!mountedRef.current || cleanedUp) return
        const img = doc.querySelector('img')
        attempts += 1
        const imageLoaded = Boolean(img?.complete && img.naturalWidth > 0)
        if (!imageLoaded && attempts < maxAttempts) {
          schedule(waitForQrImageThenPrint, 100)
          return
        }
        schedule(() => {
          if (!mountedRef.current || cleanedUp) return
          try {
            win.focus()
            win.print()
          } catch {
            cleanup()
          }
        }, 120)
      }

      schedule(waitForQrImageThenPrint, 0)
    }

    let loadHandled = false
    const onIframeLoad = () => {
      if (loadHandled) return
      loadHandled = true
      if (!mountedRef.current) {
        cleanup()
        return
      }
      const win = iframe.contentWindow
      const doc = iframe.contentDocument
      if (!win || !doc) {
        cleanup()
        return
      }
      win.addEventListener('afterprint', afterPrint, { once: true })
      runPrintWhenImageReady(win, doc)
    }

    iframe.addEventListener('load', onIframeLoad, { once: true })
    iframe.srcdoc = markup
    document.body.appendChild(iframe)
  }, [qrImageSrc, storeDeepLink, t])

  const copyDisabled =
    !storeId || showProfileLoading || !storeDeepLink

  const qrDisabled = copyDisabled

  useEffect(() => {
    clearCopyFeedbackTimer()
    setLinkCopied(false)
  }, [storeDeepLink, clearCopyFeedbackTimer])

  return (
    <div className={Styles.page}>
      <Header title={t('marketing.tools-page.title')} />
      <div className={Styles.toolsIntro}>
        <Text variant="subtitle" className={Styles.toolsDescription}>
          {t('marketing.tools-page.description')}
        </Text>
      </div>
      <section className={Styles.toolsSection}>
        <Text variant="heading-04">{t('offers.offers-title')}</Text>
        <div className={Styles.offerCards}>
          <div className={Styles.offerCard}>
            <div className={Styles.offerCardBody}>
              <Text variant="nextSubtitle" weight="semiBold">
                {t('offers.select-offer-type.item-discount.title')}
              </Text>
              <Text variant="body" className={Styles.offerCardDescription}>
                {t('offers.select-offer-type.item-discount.description')}
              </Text>
            </div>
            <div className={Styles.offerCardFooter}>
              <Button
                size="small"
                variant="primary"
                shape="rounded"
                className={Styles.offerCardButton}
                onClick={() => onCreateOffer('item_discount')}>
                {t('marketing.tools-page.create')}
                <ClickRight className={Styles.offerCardButtonIcon} />
              </Button>
              <div className={Styles.offerCardIcon} aria-hidden="true">
                <ItemDiscountIllustration className={Styles.offerCardIllustration} />
              </div>
            </div>
          </div>
          <div className={Styles.offerCard}>
            <div className={Styles.offerCardBody}>
              <Text variant="nextSubtitle" weight="semiBold">
                {t('offers.select-offer-type.cart-discount.title')}
              </Text>
              <Text variant="body" className={Styles.offerCardDescription}>
                {t('offers.select-offer-type.cart-discount.description')}
              </Text>
            </div>
            <div className={Styles.offerCardFooter}>
              <Button
                size="small"
                variant="primary"
                shape="rounded"
                className={Styles.offerCardButton}
                onClick={handleCreateCartDiscount}>
                {t('marketing.tools-page.create')}
                <ClickRight className={Styles.offerCardButtonIcon} />
              </Button>
              <div className={Styles.offerCardIcon} aria-hidden="true">
                <CartDiscountIllustration className={Styles.offerCardIllustration} />
              </div>
            </div>
          </div>
          <div className={Styles.offerCard}>
            <div className={Styles.offerCardBody}>
              <Text variant="nextSubtitle" weight="semiBold">
                {t('offers.select-offer-type.buy-x-get-y.title')}
              </Text>
              <Text variant="body" className={Styles.offerCardDescription}>
                {t('offers.select-offer-type.buy-x-get-y.description')}
              </Text>
            </div>
            <div className={Styles.offerCardFooter}>
              <Button
                size="small"
                variant="primary"
                shape="rounded"
                className={Styles.offerCardButton}
                onClick={() => onCreateOffer('buyX_getY')}>
                {t('marketing.tools-page.create')}
                <ClickRight className={Styles.offerCardButtonIcon} />
              </Button>
              <div className={Styles.offerCardIcon} aria-hidden="true">
                <BuyXGetYIllustration className={Styles.offerCardIllustration} />
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className={Styles.toolsSection}>
        <Text variant="heading-04">{t('marketing.link-sharing.title')}</Text>
        <div className={Styles.linkSharingCard}>
          <div>
            <Text variant="nextSubtitle" weight="semiBold">
              {t('marketing.link-sharing.store-link')}
            </Text>
            <Text variant="body" className={Styles.linkSharingDescription}>
              {t('marketing.link-sharing.description')}
            </Text>
          </div>
          <div className={Styles.linkSharingRow}>
            <div className={Styles.linkSharingValue}>
              {showProfileLoading ? t('marketing.link-sharing.loading') : storeDeepLink}
            </div>
            <div className={Styles.linkSharingActions}>
              <Button
                size="small"
                variant="secondary"
                shape="rounded"
                className={clsx(
                  Styles.linkSharingButton,
                  linkCopied && Styles.linkSharingButtonCopied
                )}
                onClick={handleCopyStoreLink}
                disabled={copyDisabled}
                type="button"
                aria-live="polite">
                {linkCopied ? (
                  <>
                    <CopyDone className={Styles.linkSharingCopiedIcon} />
                    {t('marketing.link-sharing.copied')}
                  </>
                ) : (
                  <>
                    <CopyIcon
                      aria-hidden
                      className={Styles.linkSharingButtonIcon}
                    />
                    {t('marketing.link-sharing.copy')}
                  </>
                )}
              </Button>
              <Button
                size="small"
                variant="secondary"
                shape="rounded"
                className={Styles.linkSharingQrButton}
                onClick={openQrDialog}
                disabled={qrDisabled}
                aria-label={t('marketing.link-sharing.qr-code')}>
                <BarCode className={Styles.linkSharingQrIcon} />
              </Button>
            </div>
          </div>
        </div>
      </section>
      {showCartDiscountError && (
        <CommonModal
          open={showCartDiscountError}
          title={t('offers.cart-discount-error.title')}
          onOpenChange={() => setShowCartDiscountError(false)}
          body={
            <Text variant="body">
              {t('offers.cart-discount-error.description')}
            </Text>
          }
          footer={
            <div className={Styles.modalFooter}>
              <Button
                onClick={() => setShowCartDiscountError(false)}
                variant="secondary">
                {t('close')}
              </Button>
              <Button variant="primary" onClick={handleGoToActiveDiscounts}>
                {t('offers.cart-discount-error.button')}
              </Button>
            </div>
          }
        />
      )}

      <Dialog open={isQrOpen} onOpenChange={setIsQrOpen}>
        <DialogContent className={Styles.qrDialogContent}>
          <DialogHeader className={Styles.qrDialogHeader}>
            <div className={Styles.qrDialogHeaderRow}>
              <DialogTitle className={Styles.qrDialogTitle}>
                {t('marketing.link-sharing.qr-dialog-title')}
              </DialogTitle>
              <DialogClose />
            </div>
          </DialogHeader>
          <div className={Styles.qrDialogMain}>
            <div className={Styles.qrVisualBlock}>
              <div className={Styles.qrImageFrame}>
                {qrImageSrc ? (
                  <img
                    src={qrImageSrc}
                    alt=""
                    width={232}
                    height={232}
                    className={Styles.qrImage}
                  />
                ) : null}
              </div>
              <span className={Styles.qrDialogLink}>{storeDeepLink}</span>
            </div>
            <div className={Styles.qrButtonsRow}>
              <Button
                type="button"
                variant="primary"
                shape="rounded"
                size="small"
                className={Styles.qrDownloadButton}
                icon={<Download className={Styles.qrFooterIconWhite} />}
                onClick={handleDownloadQr}
                disabled={!qrImageSrc}>
                {t('marketing.link-sharing.qr-dialog-download')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                shape="rounded"
                size="small"
                className={Styles.qrPrintButton}
                icon={<Printer className={Styles.qrPrintIcon} />}
                onClick={handlePrintQr}
                disabled={!qrImageSrc}>
                {t('marketing.link-sharing.qr-dialog-print')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default MarketingToolsPage
